/**
 * BulkComplianceCheck Component
 *
 * Runs compliance checks across all jobs in a project.
 * Shows summary of results and allows quick navigation to issues.
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  RefreshCw,
  Play,
  Shield,
  Loader2,
  ChevronRight,
  FileWarning,
  AlertCircle,
} from 'lucide-react';

interface JobComplianceResult {
  jobId: string;
  jobTitle: string;
  unitId?: string;
  unitLabel?: string;
  status: 'pass' | 'fail' | 'error';
  failedChecks: number;
  passedChecks: number;
  totalChecks: number;
  criticalIssues: string[];
}

interface BulkComplianceResponse {
  success: boolean;
  projectId: string;
  totalJobs: number;
  checkedJobs: number;
  passedJobs: number;
  failedJobs: number;
  errorJobs: number;
  results: JobComplianceResult[];
  timestamp: string;
}

interface BulkComplianceCheckProps {
  projectId: string;
  totalJobs: number;
}

export function BulkComplianceCheck({ projectId, totalJobs }: BulkComplianceCheckProps) {
  const navigate = useNavigate();
  const { toast } = useToast();

  // Local state
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<BulkComplianceResponse | null>(null);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  // Run bulk compliance check
  const runBulkCheck = useCallback(async () => {
    if (totalJobs === 0) {
      toast({
        title: 'No jobs to check',
        description: 'Add jobs to the project before running compliance checks.',
        variant: 'destructive',
      });
      return;
    }

    setIsRunning(true);
    setProgress(0);
    setResults(null);

    try {
      const bulkComplianceCheck = httpsCallable<
        { projectId: string },
        BulkComplianceResponse
      >(functions, 'bulkComplianceCheck');

      // Simulate progress (actual progress would come from function if streaming)
      const progressInterval = setInterval(() => {
        setProgress((prev) => Math.min(prev + 10, 90));
      }, 500);

      const response = await bulkComplianceCheck({ projectId });

      clearInterval(progressInterval);
      setProgress(100);
      setResults(response.data);
      setLastChecked(new Date());

      // Show summary toast
      const { passedJobs, failedJobs } = response.data;
      if (failedJobs === 0) {
        toast({
          title: 'All jobs passed!',
          description: `${passedJobs} job${passedJobs !== 1 ? 's' : ''} passed compliance checks.`,
        });
      } else {
        toast({
          title: 'Compliance issues found',
          description: `${failedJobs} job${failedJobs !== 1 ? 's' : ''} have compliance issues.`,
          variant: 'destructive',
        });
      }
    } catch (err: any) {
      console.error('Bulk compliance check error:', err);
      toast({
        title: 'Check failed',
        description: err?.message || 'Failed to run compliance checks.',
        variant: 'destructive',
      });
    } finally {
      setIsRunning(false);
    }
  }, [projectId, totalJobs, toast]);

  // If no jobs yet
  if (totalJobs === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Bulk Compliance Check
          </CardTitle>
          <CardDescription>
            Run compliance checks on all jobs in this project
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <FileWarning className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
            <p className="text-muted-foreground">
              No jobs to check. Add jobs to the project first.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Bulk Compliance Check
            </CardTitle>
            <CardDescription>
              Run compliance checks on all {totalJobs} job{totalJobs !== 1 ? 's' : ''} in this
              project
            </CardDescription>
          </div>
          <Button onClick={runBulkCheck} disabled={isRunning}>
            {isRunning ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Checking...
              </>
            ) : results ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Re-run Check
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                Run Check
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Running state */}
        {isRunning && (
          <div className="space-y-4">
            <div className="flex items-center justify-between text-sm">
              <span>Checking jobs...</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} />
          </div>
        )}

        {/* Results */}
        {!isRunning && results && (
          <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950/30">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <span className="text-2xl font-bold text-green-700 dark:text-green-400">
                    {results.passedJobs}
                  </span>
                </div>
                <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                  Passed
                </p>
              </div>

              <div className="p-4 rounded-lg bg-yellow-50 dark:bg-yellow-950/30">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-600" />
                  <span className="text-2xl font-bold text-yellow-700 dark:text-yellow-400">
                    {results.failedJobs}
                  </span>
                </div>
                <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-1">
                  Failed
                </p>
              </div>

              <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-950/30">
                <div className="flex items-center gap-2">
                  <XCircle className="h-5 w-5 text-gray-600" />
                  <span className="text-2xl font-bold text-gray-700 dark:text-gray-400">
                    {results.errorJobs}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Errors
                </p>
              </div>
            </div>

            {/* Last checked */}
            {lastChecked && (
              <p className="text-xs text-muted-foreground">
                Last checked: {lastChecked.toLocaleString()}
              </p>
            )}

            {/* All passed message */}
            {results.failedJobs === 0 && results.errorJobs === 0 && (
              <Alert className="border-green-200 bg-green-50/50 dark:bg-green-950/20">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertTitle className="text-green-800 dark:text-green-200">
                  All Jobs Ready
                </AlertTitle>
                <AlertDescription className="text-green-700 dark:text-green-300">
                  All {results.passedJobs} jobs have passed compliance checks and are ready
                  for submission.
                </AlertDescription>
              </Alert>
            )}

            {/* Failed jobs list */}
            {results.failedJobs > 0 && (
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-yellow-500" />
                  Jobs with Issues ({results.failedJobs})
                </h4>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {results.results
                    .filter((r) => r.status === 'fail')
                    .map((result) => (
                      <div
                        key={result.jobId}
                        className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-950/20 rounded-lg border border-yellow-200 dark:border-yellow-900 cursor-pointer hover:bg-yellow-100 dark:hover:bg-yellow-950/30 transition-colors"
                        onClick={() => navigate(`/jobs/${result.jobId}`)}
                      >
                        <div>
                          <p className="font-medium">{result.jobTitle}</p>
                          {result.unitLabel && (
                            <p className="text-sm text-muted-foreground">{result.unitLabel}</p>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="destructive" className="text-xs">
                              {result.failedChecks} issue{result.failedChecks !== 1 ? 's' : ''}
                            </Badge>
                            {result.criticalIssues.length > 0 && (
                              <span className="text-xs text-muted-foreground">
                                {result.criticalIssues[0]}
                                {result.criticalIssues.length > 1 &&
                                  ` +${result.criticalIssues.length - 1} more`}
                              </span>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Error jobs list */}
            {results.errorJobs > 0 && (
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-red-500" />
                  Jobs with Errors ({results.errorJobs})
                </h4>
                <div className="space-y-2">
                  {results.results
                    .filter((r) => r.status === 'error')
                    .map((result) => (
                      <div
                        key={result.jobId}
                        className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-950/20 rounded-lg border border-red-200 dark:border-red-900"
                      >
                        <div>
                          <p className="font-medium">{result.jobTitle}</p>
                          <p className="text-sm text-red-600 dark:text-red-400">
                            Could not run compliance check
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/jobs/${result.jobId}`)}
                        >
                          View Job
                        </Button>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Initial state */}
        {!isRunning && !results && (
          <div className="text-center py-6">
            <Shield className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
            <p className="text-muted-foreground mb-2">
              Run a compliance check to verify all jobs are ready for submission.
            </p>
            <p className="text-sm text-muted-foreground">
              This will check {totalJobs} job{totalJobs !== 1 ? 's' : ''} for missing
              documents, incomplete fields, and jurisdiction requirements.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
